import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import DateSelector from "../../../ui/DateSelector";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { toast } from "react-toastify";
import { validationSchema, CREATABLE_COUPON_TYPE_OPTIONS } from "../schema";
import { createNewCoupon } from "../../../../redux/features/CouponSlice";
import type { CreateCouponPayload } from "../../../../apis/coupons";

interface CreateCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

type CreateFormState = {
  expiry_date: string;
  couponType: string;
  couponValueType: "" | "AMOUNT" | "PERCENTAGE";
  couponAmount: string;
  couponDiscount: string;
  count: string;
  cooldownPeriod: string;
  activeDuration: string;
  vendorIncentiveType: "" | "AMOUNT" | "PERCENTAGE";
  vendorIncentiveAmount: string;
  vendorIncentivePercentage: string;
};

const initialFormState: CreateFormState = {
  expiry_date: "",
  couponType: "",
  couponValueType: "",
  couponAmount: "",
  couponDiscount: "",
  count: "1",
  cooldownPeriod: "24",
  activeDuration: "24",
  vendorIncentiveType: "",
  vendorIncentiveAmount: "",
  vendorIncentivePercentage: "",
};

const initialErrors: Record<keyof CreateFormState, string> = {
  expiry_date: "",
  couponType: "",
  couponValueType: "",
  couponAmount: "",
  couponDiscount: "",
  count: "",
  cooldownPeriod: "",
  activeDuration: "",
  vendorIncentiveType: "",
  vendorIncentiveAmount: "",
  vendorIncentivePercentage: "",
};

/** Format a Date as YYYY-MM-DD using local components (no UTC drift). */
const formatLocalYmd = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const prettifyCouponType = (raw: string): string =>
  raw
    ?.split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ") ?? "";

/**
 * Convert a YYYY-MM-DD date string into an ISO datetime at end-of-day UTC.
 * This matches the admin mental model: "expires on May 13" -> valid through
 * 23:59:59.999 UTC on May 13.
 */
const toEndOfDayIso = (dateStr: string): string => {
  if (!dateStr) return "";
  // Construct as UTC end-of-day to avoid local-timezone surprises.
  return `${dateStr}T23:59:59.999Z`;
};

const CreateCouponModal: React.FC<CreateCouponModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const dispatch = useAppDispatch();
  const { creating } = useAppSelector((state: RootState) => state.coupons);

  const [formData, setFormData] = useState<CreateFormState>(initialFormState);
  const [errors, setErrors] =
    useState<Record<keyof CreateFormState, string>>(initialErrors);

  // Reset whenever the modal opens fresh, so a previous attempt's state never lingers.
  useEffect(() => {
    if (open) {
      setFormData(initialFormState);
      setErrors(initialErrors);
    }
  }, [open]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name: keyof CreateFormState, value: string) => {
    setFormData((prev) => {
      const next: CreateFormState = { ...prev, [name]: value } as CreateFormState;
      if (name === "vendorIncentiveType") {
        if (value === "AMOUNT") next.vendorIncentivePercentage = "";
        if (value === "PERCENTAGE") next.vendorIncentiveAmount = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      return true;
    } catch (err: any) {
      const validationErrors: Record<string, string> = {};
      err.inner.forEach((error: any) => {
        if (!validationErrors[error.path]) {
          validationErrors[error.path] = error.message;
        }
      });
      setErrors((prev) => ({ ...prev, ...validationErrors }));
      return false;
    }
  };

  const buildPayload = (): CreateCouponPayload => {
    const isCouponAmount = formData.couponValueType === "AMOUNT";
    const isVendorAmount = formData.vendorIncentiveType === "AMOUNT";
    return {
      expiry_date: toEndOfDayIso(formData.expiry_date),
      couponType: formData.couponType,
      count: Number(formData.count),
      cooldownPeriod: Number(formData.cooldownPeriod),
      activeDuration: Number(formData.activeDuration),
      vendorIncentiveAmount: isVendorAmount
        ? Number(formData.vendorIncentiveAmount)
        : 0,
      vendorIncentivePercentage: isVendorAmount
        ? 0
        : Number(formData.vendorIncentivePercentage),
      couponAmount: isCouponAmount ? Number(formData.couponAmount) : 0,
      couponDiscount: isCouponAmount ? 0 : Number(formData.couponDiscount),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;

    const result = await dispatch(createNewCoupon(buildPayload()));
    if (createNewCoupon.fulfilled.match(result)) {
      toast.success(result.payload?.message || "Coupon created successfully");
      onClose();
      onSave();
    } else {
      toast.error((result.payload as string) || "Failed to create coupon");
    }
  };

  const countNumber = Number(formData.count) || 0;

  // Inline preview shown when we have enough info to summarize.
  const previewSummary = useMemo(() => {
    if (!formData.couponType || !formData.expiry_date) return null;

    const valueLabel =
      formData.couponValueType === "AMOUNT" && formData.couponAmount
        ? `₦${formData.couponAmount}`
        : formData.couponValueType === "PERCENTAGE" && formData.couponDiscount
        ? `${formData.couponDiscount}%`
        : null;

    if (!valueLabel) return null;

    return {
      count: countNumber || 1,
      type: prettifyCouponType(formData.couponType),
      value: valueLabel,
      expiry: formData.expiry_date,
    };
  }, [
    formData.couponType,
    formData.couponValueType,
    formData.couponAmount,
    formData.couponDiscount,
    formData.expiry_date,
    countNumber,
  ]);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Create Coupon"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            layout="toolbar"
            variant="ghost"
          />
          <Button
            type="submit"
            label={countNumber > 1 ? `Create ${countNumber} coupons` : "Create"}
            onClick={handleSubmit}
            isLoading={creating}
            layout="toolbar"
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <DateSelector
          label="Expiry date"
          name="expiry_date"
          value={formData.expiry_date}
          onChange={(date) => {
            setFormData((prev) => ({
              ...prev,
              expiry_date: date ? formatLocalYmd(date) : "",
            }));
            setErrors((prev) => ({ ...prev, expiry_date: "" }));
          }}
          placeholder="Select expiry date"
          error={errors.expiry_date}
          disablePast
          required
        />

        <Dropdown
          label="Coupon type"
          name="couponType"
          options={CREATABLE_COUPON_TYPE_OPTIONS.map((t) => ({
            label: prettifyCouponType(t),
            value: t,
          }))}
          placeholder="Select coupon type"
          value={formData.couponType}
          onSelect={(value) => handleSelectChange("couponType", value)}
          error={errors.couponType}
          required
        />

        <Dropdown
          label="Value type"
          name="couponValueType"
          options={[
            { label: "Amount (₦)", value: "AMOUNT" },
            { label: "Percentage (%)", value: "PERCENTAGE" },
          ]}
          placeholder="Select value type"
          value={formData.couponValueType}
          onSelect={(value) =>
            handleSelectChange("couponValueType", value as string)
          }
          error={errors.couponValueType}
          required
        />

        {formData.couponValueType === "AMOUNT" && (
          <ModalInput
            label="Coupon amount (₦)"
            name="couponAmount"
            type="number"
            value={formData.couponAmount}
            onChange={handleChange}
            placeholder="Enter coupon amount"
            error={errors.couponAmount}
            required
          />
        )}

        {formData.couponValueType === "PERCENTAGE" && (
          <ModalInput
            label="Coupon discount (%)"
            name="couponDiscount"
            type="number"
            value={formData.couponDiscount}
            onChange={handleChange}
            placeholder="Enter coupon discount"
            error={errors.couponDiscount}
            required
          />
        )}

        <ModalInput
          label="Cooldown period (hours)"
          name="cooldownPeriod"
          type="number"
          value={formData.cooldownPeriod}
          onChange={handleChange}
          placeholder="24"
          error={errors.cooldownPeriod}
          min={1}
          required
        />
        <ModalInput
          label="Active duration (hours)"
          name="activeDuration"
          type="number"
          value={formData.activeDuration}
          onChange={handleChange}
          placeholder="24"
          error={errors.activeDuration}
          min={1}
          required
        />
        <Dropdown
          label="Vendor incentive type"
          name="vendorIncentiveType"
          options={[
            { label: "Amount (₦)", value: "AMOUNT" },
            { label: "Percentage (%)", value: "PERCENTAGE" },
          ]}
          placeholder="Select vendor incentive type"
          value={formData.vendorIncentiveType}
          onSelect={(value) =>
            handleSelectChange("vendorIncentiveType", value as string)
          }
          error={errors.vendorIncentiveType}
          required
        />
        {formData.vendorIncentiveType === "AMOUNT" && (
          <ModalInput
            label="Vendor incentive amount (₦)"
            name="vendorIncentiveAmount"
            type="number"
            value={formData.vendorIncentiveAmount}
            onChange={handleChange}
            placeholder="0"
            error={errors.vendorIncentiveAmount}
            min={0}
            required
          />
        )}
        {formData.vendorIncentiveType === "PERCENTAGE" && (
          <ModalInput
            label="Vendor incentive (%)"
            name="vendorIncentivePercentage"
            type="number"
            value={formData.vendorIncentivePercentage}
            onChange={handleChange}
            placeholder="0"
            error={errors.vendorIncentivePercentage}
            min={0}
            max={100}
            required
          />
        )}

        <ModalInput
          label="Number of coupons"
          name="count"
          type="number"
          value={formData.count}
          onChange={handleChange}
          placeholder="1"
          error={errors.count}
          min={1}
          required
        />

        {previewSummary && (
          <div className="mt-4 rounded-lg border border-primary/20 bg-lightPurple/40 p-3 text-xs text-gray-700">
            <span className="font-semibold text-accent">About to create:</span>{" "}
            {previewSummary.count}{" "}
            <span className="font-semibold">{previewSummary.type}</span> coupon
            {previewSummary.count > 1 ? "s" : ""} worth{" "}
            <span className="font-semibold">{previewSummary.value}</span>,
            expiring on{" "}
            <span className="font-semibold">{previewSummary.expiry}</span>.
          </div>
        )}
      </form>
    </CustomModalContainer>
  );
};

export default CreateCouponModal;
